import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    IconButton,
    InputBase,
    Tooltip
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import { useSnackbar } from 'notistack';
import LoadingPanel from '../common/LoadingPanel';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import { getOrCreateToken, regenerateToken } from '@/service/sdeskTokenService';
import './UserProfile.css';
import { buildSnackbarMessage } from '@/component/common/SnackbarMessage';

const MASKED_TOKEN = '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022';

const buildConfig = (connectionUrl, token) => ({
    'sdesk-mcp': {
        command: 'npx',
        args: ['-y', 'mcp-remote', connectionUrl, '--header', `X-SDToken:${token}`],
        disabled: false,
        autoApprove: ['get_log_schema', 'search_logs']
    }
});

export default function McpTokenTab({ organizationId, projectId, userId }) {
    // Re-render when the translation dictionary loads/updates
    useTranslationSubscription();

    const { enqueueSnackbar } = useSnackbar();
    const requestIdRef = useRef(0);
    const [rawToken, setRawToken] = useState('');
    const [connectionUrl, setConnectionUrl] = useState('');
    const [isTokenVisible, setIsTokenVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const identity = useMemo(
        () => ({ organizationId, projectId, userId }),
        [organizationId, projectId, userId]
    );
    const displayToken = isTokenVisible ? rawToken : MASKED_TOKEN;
    const displayConfig = useMemo(
        () => JSON.stringify(buildConfig(connectionUrl, displayToken), null, 2),
        [connectionUrl, displayToken]
    );

    const showError = () => enqueueSnackbar(buildSnackbarMessage('MCP_TOKEN_ERROR'), {
        variant: 'error',
        autoHideDuration: 3000
    });

    useEffect(() => {
        const currentRequestId = requestIdRef.current + 1;
        requestIdRef.current = currentRequestId;
        setRawToken('');
        setConnectionUrl('');
        setIsTokenVisible(false);
        setIsConfirmOpen(false);
        setIsLoading(true);

        const notifyError = () => enqueueSnackbar(buildSnackbarMessage('MCP_TOKEN_ERROR'), {
            variant: 'error',
            autoHideDuration: 3000
        });

        getOrCreateToken(identity)
            .then(result => {
                if (requestIdRef.current !== currentRequestId) return;
                if (!result?.success || !result?.data?.token || !result?.data?.connectionUrl) {
                    notifyError();
                    return;
                }
                setRawToken(result.data.token);
                setConnectionUrl(result.data.connectionUrl);
            })
            .catch(() => {
                if (requestIdRef.current === currentRequestId) notifyError();
            })
            .finally(() => {
                if (requestIdRef.current === currentRequestId) setIsLoading(false);
            });

        return () => {
            if (requestIdRef.current === currentRequestId) requestIdRef.current += 1;
        };
        // enqueueSnackbar is stable and identity contains all reload dependencies.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identity]);

    const copyValue = async value => {
        try {
            if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
            await navigator.clipboard.writeText(value);
            enqueueSnackbar(buildSnackbarMessage('MCP_TOKEN_COPIED'), {
                variant: 'success',
                autoHideDuration: 3000
            });
        } catch (error) {
            enqueueSnackbar(buildSnackbarMessage('MCP_TOKEN_COPY_ERROR'), {
                variant: 'error',
                autoHideDuration: 3000
            });
        }
    };

    const handleRegenerate = async () => {
        if (isLoading) return;
        const currentRequestId = requestIdRef.current;
        setIsLoading(true);
        setIsConfirmOpen(false);
        try {
            const result = await regenerateToken(identity);
            if (requestIdRef.current !== currentRequestId) return;
            if (!result?.success || !result?.data?.token) {
                showError();
                setIsConfirmOpen(false);
                return;
            }
            setRawToken(result.data.token);
            if (result.data.connectionUrl) setConnectionUrl(result.data.connectionUrl);
            setIsTokenVisible(false);
            setIsConfirmOpen(false);
        } catch (error) {
            if (requestIdRef.current === currentRequestId) showError();
        } finally {
            if (requestIdRef.current === currentRequestId) setIsLoading(false);
        }
    };

    const copyRawConfig = () => {
        const rawConfig = JSON.stringify(buildConfig(connectionUrl, rawToken), null, 2);
        return copyValue(rawConfig);
    };

    const confirmMessage = (
        <>
            <div>{trI18n('MCP_TOKEN_REISSUE_MESSAGE_1')}</div>
            <div className="mcpToken-confirmWarning">{trI18n('MCP_TOKEN_REISSUE_MESSAGE_2')}</div>
        </>
    );

    return (
        <div className="settingFrame scrollTabMcp">
            <LoadingPanel isLoading={isLoading} />
            <div className="mcpTokenTab">
                {/* Section 1: Connection Info */}
                <section className="mcpToken-section">
                    <h3>{trI18n('MCP_TOKEN_CONNECTION_INFO')}</h3>
                    <p className="mcpToken-description">{trI18n('MCP_TOKEN_CONNECTION_DESCRIPTION')}</p>
                    <label className="mcpToken-label">{trI18n('MCP_TOKEN_CONNECTION_URL')}</label>
                    <div className="mcpToken-fieldRow">
                        <Tooltip title={connectionUrl || ''}>
                            <div className="mcpToken-inputBox">
                                <InputBase
                                    fullWidth
                                    readOnly
                                    value={connectionUrl}
                                    className="mcpToken-input"
                                />
                            </div>
                        </Tooltip>
                        <Tooltip title={trI18n('MCP_TOKEN_COPY')}>
                            <IconButton
                                size="small"
                                className="mcpToken-iconBtn"
                                onClick={() => copyValue(connectionUrl)}
                                disabled={isLoading || !connectionUrl}
                                aria-label={trI18n('MCP_TOKEN_COPY')}
                            >
                                <img src="/assets/image/icon_copy-mcp.svg" alt="" width="27" height="27" />
                            </IconButton>
                        </Tooltip>
                    </div>
                </section>

                {/* Section 2: Access Token */}
                <section className="mcpToken-section">
                    <h3>{trI18n('MCP_TOKEN_ACCESS_TOKEN')}</h3>
                    <p className="mcpToken-description">{trI18n('MCP_TOKEN_ACCESS_DESCRIPTION')}</p>
                    <label className="mcpToken-label">{trI18n('MCP_TOKEN_X_SD_TOKEN')}</label>
                    <div className="mcpToken-fieldRow">
                        <Tooltip key={isTokenVisible ? 'visible' : 'hidden'} title={isTokenVisible ? rawToken : ''} disableHoverListener={!isTokenVisible}>
                            <div className="mcpToken-inputBox">
                                <InputBase
                                    fullWidth
                                    readOnly
                                    value={rawToken ? displayToken : ''}
                                    className="mcpToken-input"
                                />
                                <IconButton
                                    size="small"
                                    className="mcpToken-eyeBtn"
                                    aria-label={trI18n(isTokenVisible ? 'MCP_TOKEN_HIDE' : 'MCP_TOKEN_SHOW')}
                                    onClick={() => setIsTokenVisible(value => !value)}
                                    disabled={!rawToken}
                                >
                                    {isTokenVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                            </div>
                        </Tooltip>
                        <Tooltip title={trI18n('MCP_TOKEN_COPY')}>
                            <IconButton
                                size="small"
                                className="mcpToken-iconBtn"
                                onClick={() => copyValue(rawToken)}
                                disabled={isLoading || !rawToken}
                                aria-label={trI18n('MCP_TOKEN_COPY')}
                            >
                                <img src="/assets/image/icon_copy-mcp.svg" alt="" width="27" height="27" />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="contained"
                            size="small"
                            className="mcpToken-reissueBtn"
                            onClick={() => setIsConfirmOpen(true)}
                            disabled={isLoading || !rawToken}
                        >
                            {trI18n('MCP_TOKEN_REISSUE')}
                        </Button>
                    </div>
                </section>

                {/* Section 3: Config Sample */}
                <section className="mcpToken-section mcpToken-section--last">
                    <h3>{trI18n('MCP_TOKEN_CONFIG_SAMPLE')}</h3>
                    <div className="mcpToken-configDescRow">
                        <p className="mcpToken-description mcpToken-description--inline">{trI18n('MCP_TOKEN_CONFIG_DESCRIPTION')}</p>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            className="mcpToken-copyAllBtn"
                            onClick={copyRawConfig}
                            disabled={isLoading || !rawToken || !connectionUrl}
                        >
                            {trI18n('MCP_TOKEN_COPY_ALL')}
                        </Button>
                    </div>
                    <pre className="mcpToken-config">{displayConfig}</pre>
                </section>
            </div>

            <ConfirmDialog
                isOpen={isConfirmOpen}
                msgConfirm={confirmMessage}
                closeCallBack={() => setIsConfirmOpen(false)}
                okCallBack={handleRegenerate}
                textClose={trI18n('MCP_TOKEN_CANCEL')}
                textSubmit={trI18n('MCP_TOKEN_REISSUE_CONFIRM')}
                disableButtonCallBack={isLoading}
                okButtonStyle={{ backgroundColor: '#CC4733', color: '#FFFFFF', padding: '3px 8px'}}
            />
        </div>
    );
}
